import type {
  CampaignGiftDetailData,
  CampaignOrder,
  CampaignOrderProduct,
  CampaignGiftDetailRequest,
  CampaignGiftDetailResponse,
  CategoryTreeResponse,
  CreatorGiftingOverrideRequest,
  CreatorGiftingOverrideResponse,
  GiftingCollectionCreateRequest,
  GiftingCollectionListItem,
  GiftingCollectionProductsGetResponse,
  GiftingCollectionProductsRequest,
  GiftingCollectionProductsSaveResponse,
  GiftingCollectionResponse,
  GiftingCollectionUpdateRequest,
  GiftingOrderUpdateRequest,
  OCAPIProductDetailResponse,
  ProductSearchRequest,
  ProductSearchResponse,
  SendGiftingInvitesRequest,
  _HydratedCollectionProduct,
} from '@dashhudson/api-client/creator/model';

import { createDashHttpClient, type HttpRequestConfig } from '@/apis/dash.http';
import { env } from '@/env';

export const http = createDashHttpClient(env.creatorsApiUrl, {
  camelizeKeys: true,
});

export type GiftDetail = CampaignGiftDetailData;
export type GiftingCollection = GiftingCollectionListItem;
export type CampaignGiftOrder = CampaignOrder;
export type CampaignGiftOrderProduct = CampaignOrderProduct;
export type HydratedCollectionProduct = _HydratedCollectionProduct;

export const GIFT_ORDER_STATUS = {
  SUBMITTED: 'submitted',
  APPROVED: 'approved',
  PLACING: 'placing',
  ORDER_PLACED: 'order_placed',
  FAILED: 'failed',
} as const;

// The backend has a limit of 100 orders per approve request.
export const APPROVE_ORDERS_MAX = 100;

export const GIFT_ORDER_PRODUCT_STATUS = {
  OK: 'ok',
  OUT_OF_STOCK: 'out_of_stock',
  NOT_LIVE: 'not_live',
  DELETED: 'deleted',
} as const satisfies Record<string, NonNullable<CampaignGiftOrderProduct['status']>>;

export async function getGiftDetail(
  { brandId, campaignId }: { brandId: number; campaignId: number },
  config: HttpRequestConfig = {},
) {
  return http.get<{ data: GiftDetail }>(
    `/brands/${brandId}/gifting/campaigns/${campaignId}/gift_detail`,
    config,
  );
}

export async function upsertGiftDetail(
  { brandId, campaignId }: { brandId: number; campaignId: number },
  body: CampaignGiftDetailRequest,
  config: HttpRequestConfig = {},
) {
  return http.put<CampaignGiftDetailResponse>(
    `/brands/${brandId}/gifting/campaigns/${campaignId}/gift_detail`,
    body,
    config,
  );
}

export async function updateGiftDetail(
  { brandId, campaignId, ordersOpen }: { brandId: number; campaignId: number; ordersOpen: boolean },
  config: HttpRequestConfig = {},
) {
  return http.put<{ data: GiftDetail }>(
    `/brands/${brandId}/gifting/campaigns/${campaignId}/gift_detail`,
    { ordersOpen },
    config,
  );
}

export async function upsertCreatorGiftingOverride(
  { brandId, campaignId, creatorId }: { brandId: number; campaignId: number; creatorId: number },
  body: CreatorGiftingOverrideRequest,
  config: HttpRequestConfig = {},
) {
  return http.put<CreatorGiftingOverrideResponse>(
    `/brands/${brandId}/gifting/campaigns/${campaignId}/creator_overrides/${creatorId}`,
    body,
    config,
  );
}

export async function getGiftingCollections(
  { brandId, campaignId }: { brandId: number; campaignId: number },
  config: HttpRequestConfig = {},
) {
  return http.get<{ data: GiftingCollection[] }>(
    `/brands/${brandId}/gifting/campaigns/${campaignId}/collections`,
    config,
  );
}

export async function createGiftingCollection(
  { brandId }: { brandId: number },
  body: GiftingCollectionCreateRequest,
  config: HttpRequestConfig = {},
) {
  return http.post<GiftingCollectionResponse>(
    `/brands/${brandId}/gifting/collections`,
    body,
    config,
  );
}

export async function updateGiftingCollection(
  { brandId, collectionId }: { brandId: number; collectionId: number },
  body: GiftingCollectionUpdateRequest,
  config: HttpRequestConfig = {},
) {
  return http.patch<GiftingCollectionResponse>(
    `/brands/${brandId}/gifting/collections/${collectionId}`,
    body,
    config,
  );
}

export async function getCampaignGiftOrders(
  { brandId, campaignId }: { brandId: number; campaignId: number },
  config: HttpRequestConfig = {},
) {
  return http.get<{ data: CampaignGiftOrder[] }>(
    `/brands/${brandId}/gifting/campaigns/${campaignId}/orders`,
    config,
  );
}

export async function sendGiftingInvites(
  { brandId }: { brandId: number },
  body: SendGiftingInvitesRequest,
  config: HttpRequestConfig = {},
) {
  return http.post<void>(`/brands/${brandId}/gifting/invite`, body, config);
}

export async function approveGiftOrders(
  { brandId }: { brandId: number },
  { orderIds }: { orderIds: number[] },
  config: HttpRequestConfig = {},
) {
  return http.post<void>(`/brands/${brandId}/gifting/orders/approve`, { orderIds }, config);
}

export async function updateGiftOrder(
  { brandId, orderId }: { brandId: number; orderId: number },
  body: GiftingOrderUpdateRequest,
  config: HttpRequestConfig = {},
) {
  return http.patch<void>(`/brands/${brandId}/gifting/orders/${orderId}`, body, config);
}

export async function deleteGiftingCollection(
  { brandId, collectionId }: { brandId: number; collectionId: number },
  config: HttpRequestConfig = {},
) {
  return http.delete<void>(`/brands/${brandId}/gifting/collections/${collectionId}`, config);
}

export async function getGiftingCollectionProducts(
  { brandId, collectionId }: { brandId: number; collectionId: number },
  config: HttpRequestConfig = {},
) {
  return http.get<GiftingCollectionProductsGetResponse>(
    `/brands/${brandId}/gifting/collections/${collectionId}/products`,
    config,
  );
}

export async function saveGiftingCollectionProducts(
  { brandId, collectionId }: { brandId: number; collectionId: number },
  body: GiftingCollectionProductsRequest,
  config: HttpRequestConfig = {},
) {
  return http.put<GiftingCollectionProductsSaveResponse>(
    `/brands/${brandId}/gifting/collections/${collectionId}/products`,
    body,
    config,
  );
}

export async function getGiftingCategories(
  { brandId }: { brandId: number },
  config: HttpRequestConfig = {},
) {
  return http.get<CategoryTreeResponse>(`/brands/${brandId}/gifting/ocapi/categories`, config);
}

export async function searchGiftingProducts(
  { brandId }: { brandId: number },
  body: ProductSearchRequest,
  config: HttpRequestConfig = {},
) {
  return http.post<ProductSearchResponse>(
    `/brands/${brandId}/gifting/ocapi/product_search`,
    body,
    config,
  );
}

export async function getGiftingProduct(
  { brandId, productId }: { brandId: number; productId: string },
  config: HttpRequestConfig = {},
) {
  return http.get<OCAPIProductDetailResponse>(
    `/brands/${brandId}/gifting/ocapi/products/${productId}`,
    config,
  );
}
